import {
  Box,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { CashierField } from '../../../../../../components/Inputs/CashierField';
import { CashRegisterField } from '../../../../../../components/Inputs/CashRegisterField';
import { OperationField } from '../../../../../../components/Inputs/OperationField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { CashRegisterReportFormModel } from '../../models';

export const FilterForm: FC<{
  onSubmit: (data: CashRegisterReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<CashRegisterReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Cash Register Report" />
        <Stack direction={'row'} spacing={0}>
          <Stack
            direction={'column'}
            spacing={2}
            sx={{
              p: 3,
            }}
          >
            <DateRangePicker
              value={dates}
              onChange={handleChangeDates}
              renderInput={({ startProps, endProps }) => (
                <Stack
                  direction="row"
                  spacing="1rem"
                  sx={{
                    width: '100%',
                  }}
                >
                  <TextField
                    {...startProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="From"
                    error={Boolean(errors?.from)}
                    helperText={errors?.from?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    {...endProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="To"
                    error={Boolean(errors?.to)}
                    helperText={errors?.to?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              )}
            />
            <Controller
              control={control}
              name="cash_register_ids"
              render={({ field }) => (
                <CashRegisterField
                  value={field.value || []}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
            <Controller
              control={control}
              name="cashier_ids"
              render={({ field }) => (
                <CashierField value={field.value || []} onChange={value => field.onChange(value)} />
              )}
            />
            <Controller
              control={control}
              name="operation_id"
              render={({ field }) => (
                <OperationField value={field.value} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="with_exchange"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      With exchange
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
            <Controller
              control={control}
              name="with_details"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      With details
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
            <Controller
              control={control}
              name="closing_groupedby_group"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Closing grouped by group
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
            <Box sx={{ height: 10 }} />
          </Stack>
        </Stack>

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
